import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { Line, Group, Circle, Text, Rect } from 'react-konva';
import Konva from 'konva';
import { Edge, EdgeType, EdgeRoutingMode, Node, Point, ThemePalette, KonvaMouseEvent, EdgeMessage } from '../../types';
import { FONT_FAMILY, scaleFont } from '../../constants/typography';
import { EDGE } from '../../constants/edges';
import { getEdgeGeometryWithRouting, sampleDisplayPolyline } from '../../utils/edgeMath';
import type { JumperPoint, RoutedOrthoEdge } from '../../utils/edgeMath';
import { roundCorners } from '../../utils/polyline';
import { brightenColor, toRgba } from '../../utils/color';
import { tintedStroke } from '../../utils/edgeTint';
import { measureLabelSize } from '../../utils/measureLabel';
import { LABEL_HALO_ALPHA } from '../nodes/HaloText';

const DASH_PATTERNS = EDGE.DASH_PATTERNS;

/**
 * Radius of the arc an orthogonal edge traces through a turn. Small enough that
 * the run still reads as a straight corridor, large enough that the corner is
 * not a pixel notch. Every turn on a route gets the same one, so a fan of edges
 * turning together stays visibly parallel through the bend.
 */
const CORNER_RADIUS = 9;

/** Matches SocketComponent's output/input polarity so hover reveals the same story. */
const EDGE_TAIL_COLOR = '#818cf8';
const EDGE_HEAD_COLOR = '#10b981';

function makeArrow(tip: { x: number; y: number }, penultimate: { x: number; y: number }, len = 10): number[] {
  const angle = Math.atan2(tip.y - penultimate.y, tip.x - penultimate.x);
  return [
    tip.x, tip.y,
    tip.x - len * Math.cos(angle - Math.PI / 7),
    tip.y - len * Math.sin(angle - Math.PI / 7),
    tip.x - len * Math.cos(angle + Math.PI / 7),
    tip.y - len * Math.sin(angle + Math.PI / 7),
    tip.x, tip.y,
  ];
}

function makeDiamondHead(tip: { x: number; y: number }, along: { x: number; y: number }, len = 12): number[] {
  const angle = Math.atan2(tip.y - along.y, tip.x - along.x);
  const backX = tip.x - Math.cos(angle) * len;
  const backY = tip.y - Math.sin(angle) * len;
  const midX = tip.x - Math.cos(angle) * (len / 2);
  const midY = tip.y - Math.sin(angle) * (len / 2);
  const nx = -Math.sin(angle) * (len * 0.38);
  const ny = Math.cos(angle) * (len * 0.38);
  return [tip.x, tip.y, midX + nx, midY + ny, backX, backY, midX - nx, midY - ny];
}

interface EdgeComponentProps {
  edge: Edge;
  sourceNode: Node;
  targetNode: Node;
  isSelected: boolean;
  isWaypointCandidate?: boolean;
  onEdgeClick: (edgeId: string, shiftKey?: boolean) => void;
  onEdgeContextMenu?: (edgeId: string, x: number, y: number) => void;
  onWaypointUpdate?: (edgeId: string, waypoints: Point[]) => void;
  onEdgeMessageUpdate?: (edgeId: string, messages: EdgeMessage[]) => void;
  /** canvasCX/CY = message box centre in canvas coords; boxW/H = box size in canvas units */
  onEdgeMessageDblClick?: (edgeId: string, msgId: string, canvasCX: number, canvasCY: number, boxW: number, boxH: number) => void;
  editingMsgId?: string;
  onEdgeDblClick?: (edgeId: string) => void;
  discoveryMode?: boolean;
  palette: ThemePalette;
  opacity?: number;         // for flow dimming
  forceAnimated?: boolean;  // flow mode auto-animation override
  globalRoutingMode?: EdgeRoutingMode;
  allNodes?: Node[];
  jumperPoints?: JumperPoint[];
  forceControlPoints?: { cp1: Point; cp2: Point } | null;
  orthoMidShift?: number;
  /** Path routed with every other edge, so shared corridors are already separated. */
  routedPath?: RoutedOrthoEdge;
  isReadOnly?: boolean;
  /** The page's label multiplier. 1 leaves every size exactly as authored. */
  textScale?: number;
  /** Colour of the frame this wire lands in, when it leaves its own. */
  tint?: string;
}

export const EdgeComponent: React.FC<EdgeComponentProps> = ({
  edge,
  sourceNode,
  targetNode,
  isSelected,
  isWaypointCandidate = false,
  onEdgeClick,
  onEdgeContextMenu,
  onWaypointUpdate,
  onEdgeMessageUpdate,
  onEdgeMessageDblClick,
  editingMsgId,
  onEdgeDblClick,
  discoveryMode = false,
  palette,
  opacity = 1,
  forceAnimated = false,
  globalRoutingMode = 'bezier',
  allNodes = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  jumperPoints: _jumperPoints = [],
  forceControlPoints = null,
  orthoMidShift = 0,
  routedPath,
  isReadOnly = false,
  textScale = 1,
  tint,
}) => {
  const flowRef = useRef<Konva.Line>(null);
  const glowRef = useRef<Konva.Line>(null);
  const rafRef = useRef<number>(0);
  const [hovered, setHovered] = useState(false);

  const { endPoint, startPoint, linePoints, useTension, arrowPenultimate, labelPos, isOrthogonal } = useMemo(() => {
    const sourceSocket = sourceNode.sockets.find((s) => s.id === edge.sourceSocketId);
    const targetSocket = targetNode.sockets.find((s) => s.id === edge.targetSocketId);

    if (!sourceSocket || !targetSocket) {
      return { endPoint: { x: 0, y: 0 }, startPoint: { x: 0, y: 0 }, linePoints: [], useTension: false, arrowPenultimate: { x: 0, y: 0 }, labelPos: null, isOrthogonal: false };
    }

    if (routedPath && !edge.waypoints?.length) {
      const pts = routedPath.linePoints;
      return {
        ...routedPath,
        useTension: false,
        isOrthogonal: true as const,
        startPoint: pts.length >= 2 ? { x: pts[0], y: pts[1] } : { x: 0, y: 0 },
      };
    }

    const geom = getEdgeGeometryWithRouting(edge, sourceNode, targetNode, sourceSocket, targetSocket, globalRoutingMode, allNodes, orthoMidShift);

    // Apply force-adjusted control points for bezier edges
    if (forceControlPoints && !geom.isOrthogonal && !geom.useTension && geom.linePoints.length === 8) {
      const pts = [...geom.linePoints];
      pts[2] = forceControlPoints.cp1.x;
      pts[3] = forceControlPoints.cp1.y;
      pts[4] = forceControlPoints.cp2.x;
      pts[5] = forceControlPoints.cp2.y;
      const sp = { x: pts[0], y: pts[1] };
      const arrowPen = { x: pts[4], y: pts[5] };
      const t = 0.5, mt = 0.5;
      const lx = mt*mt*mt*pts[0] + 3*mt*mt*t*pts[2] + 3*mt*t*t*pts[4] + t*t*t*pts[6];
      const ly = mt*mt*mt*pts[1] + 3*mt*mt*t*pts[3] + 3*mt*t*t*pts[5] + t*t*t*pts[7];
      return { linePoints: pts, useTension: false, arrowPenultimate: arrowPen, endPoint: geom.endPoint, labelPos: { x: lx, y: ly }, startPoint: sp, isOrthogonal: false };
    }

    const pts = geom.linePoints;
    const sp = pts.length >= 2 ? { x: pts[0], y: pts[1] } : { x: 0, y: 0 };
    return { ...geom, startPoint: sp };
  }, [edge, sourceNode, targetNode, globalRoutingMode, allNodes, forceControlPoints, orthoMidShift, routedPath]);

  // Discovery mode annotation text
  const discoveryText = useMemo(() => {
    if (!discoveryMode) return null;
    const srcSock = sourceNode.sockets.find(s => s.id === edge.sourceSocketId);
    const tgtSock = targetNode.sockets.find(s => s.id === edge.targetSocketId);
    const parts: string[] = [];
    if (edge.label) parts.push(edge.label);
    const srcLabel = srcSock?.label ?? srcSock?.id ?? '?';
    const tgtLabel = tgtSock?.label ?? tgtSock?.id ?? '?';
    parts.push(`${srcLabel} \u2192 ${tgtLabel}`);
    if (edge.animated) parts.push('\u26a1');
    return parts.join(' \u00b7 ');
  }, [discoveryMode, edge, sourceNode, targetNode]);

  // RAF-based flow animation — no React state, zero re-renders
  const shouldAnimate = edge.animated || forceAnimated;
  useEffect(() => {
    if (!shouldAnimate) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    let offset = 0;
    const animate = () => {
      if (!flowRef.current) return;
      offset -= 0.5;
      flowRef.current.dashOffset(offset);
      flowRef.current.getLayer()?.batchDraw();
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [shouldAnimate]);

  const handleClick = useCallback((e: KonvaMouseEvent) => { onEdgeClick(edge.id, e.evt?.shiftKey ?? false); }, [edge.id, onEdgeClick]);

  const handleDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onEdgeDblClick?.(edge.id);
  }, [edge.id, onEdgeDblClick]);

  const handleContextMenu = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.preventDefault();
    onEdgeContextMenu?.(edge.id, e.evt.clientX, e.evt.clientY);
  }, [edge.id, onEdgeContextMenu]);

  const edgeType: EdgeType = edge.edgeType ?? 'arrow';
  const isInheritance = edgeType === 'inheritance';
  const isDiamondStart = edgeType === 'aggregation' || edgeType === 'composition';
  const sw = edge.strokeWidth ?? 1.5;
  const arrowLen = Math.max(12, sw * 4.5);
  const arrowOffset = 6; // distance between tip and socket

  // Forward arrow (target end) — shown for 'arrow' and 'bidirectional'
  const forwardArrow = useMemo(() => {
    if (edgeType === 'no-arrow' || isInheritance || isDiamondStart || linePoints.length < 4) return [];
    const angle = Math.atan2(endPoint.y - arrowPenultimate.y, endPoint.x - arrowPenultimate.x);
    const tip = {
      x: endPoint.x - Math.cos(angle) * arrowOffset,
      y: endPoint.y - Math.sin(angle) * arrowOffset
    };
    return makeArrow(tip, arrowPenultimate, arrowLen);
  }, [edgeType, isInheritance, isDiamondStart, endPoint, arrowPenultimate, linePoints.length, arrowLen]);

  const inheritanceHead = useMemo(() => {
    if (!isInheritance || linePoints.length < 4) return [];
    const angle = Math.atan2(endPoint.y - arrowPenultimate.y, endPoint.x - arrowPenultimate.x);
    const tip = {
      x: endPoint.x - Math.cos(angle) * arrowOffset,
      y: endPoint.y - Math.sin(angle) * arrowOffset,
    };
    return makeArrow(tip, arrowPenultimate, arrowLen + 2);
  }, [isInheritance, endPoint, arrowPenultimate, linePoints.length, arrowLen]);

  const sourceDiamond = useMemo(() => {
    if (!isDiamondStart || linePoints.length < 4) return [];
    const secondX = linePoints[2] ?? linePoints[0];
    const secondY = linePoints[3] ?? linePoints[1];
    const angle = Math.atan2(startPoint.y - secondY, startPoint.x - secondX);
    const tip = {
      x: startPoint.x - Math.cos(angle) * arrowOffset,
      y: startPoint.y - Math.sin(angle) * arrowOffset,
    };
    return makeDiamondHead(tip, { x: secondX, y: secondY }, arrowLen);
  }, [isDiamondStart, startPoint, linePoints, arrowLen]);

  // Reverse arrow (source end) — shown for 'bidirectional'
  const reverseArrow = useMemo(() => {
    if (edgeType !== 'bidirectional' || linePoints.length < 4) return [];
    const secondX = linePoints[2] ?? linePoints[0];
    const secondY = linePoints[3] ?? linePoints[1];
    const angle = Math.atan2(startPoint.y - secondY, startPoint.x - secondX);
    const tip = {
      x: startPoint.x - Math.cos(angle) * arrowOffset,
      y: startPoint.y - Math.sin(angle) * arrowOffset
    };
    return makeArrow(tip, { x: secondX, y: secondY }, arrowLen);
  }, [edgeType, startPoint, linePoints, arrowLen]);

  // Shorten the drawn line so it stops exactly at the arrow base (no overlap)
  // Only for plain directed flow: typed UML ends carry their own notation and
  // a bidirectional edge has no single tail.
  const showEndpointCaps = isOrthogonal
    && forwardArrow.length > 0
    && reverseArrow.length === 0
    && inheritanceHead.length === 0
    && sourceDiamond.length === 0;

  // Turns are drawn as arcs, but only the drawing changes: hit testing, arrow
  // angles and label anchors keep working off the corners the router produced.
  const shapedLinePoints = useMemo(
    () => (isOrthogonal ? roundCorners(linePoints, CORNER_RADIUS) : linePoints),
    [isOrthogonal, linePoints],
  );

  const drawnLinePoints = useMemo(() => {
    if (edgeType === 'no-arrow' || shapedLinePoints.length < 4) return shapedLinePoints;
    const angle = Math.atan2(endPoint.y - arrowPenultimate.y, endPoint.x - arrowPenultimate.x);
    const pull = (isInheritance || edgeType === 'arrow' || edgeType === 'bidirectional') ? (arrowLen + arrowOffset) : 0;
    if (!pull) return shapedLinePoints;
    const stopX = endPoint.x - Math.cos(angle) * pull;
    const stopY = endPoint.y - Math.sin(angle) * pull;
    return [...shapedLinePoints.slice(0, -2), stopX, stopY];
  }, [edgeType, isInheritance, shapedLinePoints, endPoint, arrowPenultimate, arrowLen]);

  // Shorten start for bidirectional / UML diamonds
  const drawnLinePointsBi = useMemo(() => {
    if ((edgeType !== 'bidirectional' && !isDiamondStart) || drawnLinePoints.length < 4) return drawnLinePoints;
    const secondX = shapedLinePoints[2] ?? shapedLinePoints[0];
    const secondY = shapedLinePoints[3] ?? shapedLinePoints[1];
    const angle = Math.atan2(startPoint.y - secondY, startPoint.x - secondX);
    const stopX = startPoint.x - Math.cos(angle) * (arrowLen + arrowOffset);
    const stopY = startPoint.y - Math.sin(angle) * (arrowLen + arrowOffset);
    return [stopX, stopY, ...drawnLinePoints.slice(2)];
  }, [edgeType, isDiamondStart, drawnLinePoints, startPoint, shapedLinePoints, arrowLen]);

  const finalLinePoints = (edgeType === 'bidirectional' || isDiamondStart) ? drawnLinePointsBi : drawnLinePoints;

  // Recache glow on every point change so it follows the edge during node drag.
  // Must depend on the serialised points string, not just .length, to catch mid-drag updates.
  const finalPointsKey = finalLinePoints.join(',');
  useEffect(() => {
    const node = glowRef.current;
    if (!node || !isSelected) return;
    node.filters([Konva.Filters.Blur]);
    node.blurRadius(10);
    node.cache();
  }, [isSelected, edge.id, finalPointsKey]);

  // A colour the author picked is the whole story; without one the wire falls
  // back to the neutral, pulled toward the frame it lands in.
  const strokeOf = (neutral: string) => edge.color ?? tintedStroke(neutral, tint);
  const baseColor = isWaypointCandidate ? '#f59e0b' : strokeOf(palette.edgeDefault);
  const edgeColor = isSelected ? brightenColor(baseColor, 0.45) : baseColor;
  const headColor = isWaypointCandidate ? '#f59e0b' : strokeOf(palette.edgeArrow);
  const arrowColor = isSelected ? brightenColor(headColor, 0.45) : headColor;
  const dashPat = DASH_PATTERNS[edge.dashStyle ?? 'solid'];

  const useBezier = !isOrthogonal && !useTension && linePoints.length > 4;
  const hitLinePoints = useMemo(
    () => sampleDisplayPolyline(linePoints, useBezier),
    [linePoints, useBezier],
  );

  if (linePoints.length === 0) return null;

  const waypoints = edge.waypoints ?? [];

  return (
    <Group onClick={handleClick} onDblClick={handleDblClick} onContextMenu={handleContextMenu}
      onMouseEnter={() => (discoveryMode || isSelected) && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      opacity={opacity}
    >
      {/* Wide invisible hit area */}
      <Line
        points={hitLinePoints}
        stroke="transparent" strokeWidth={Math.max(22, sw + 16)}
        bezier={false} tension={0}
        hitStrokeWidth={Math.max(22, sw + 16)}
        perfectDrawEnabled={false}
      />


      {/* Selection glow — white blurred halo, semi-transparent */}
      {isSelected && (
        <Line
          ref={glowRef}
          points={finalLinePoints}
          stroke="white"
          strokeWidth={sw + 2}
          opacity={0.25}
          bezier={useBezier} tension={useTension ? 0.4 : 0}
          listening={false}
        />
      )}

      {/* Main edge line — colour UNCHANGED when selected */}
      <Line
        points={finalLinePoints}
        strokeLinearGradientStartPoint={startPoint}
        strokeLinearGradientEndPoint={endPoint}
        strokeLinearGradientColorStops={[0, edgeColor, 1, brightenColor(edgeColor, 0.3)]}
        strokeWidth={sw}
        dash={dashPat}
        bezier={useBezier} tension={useTension ? 0.4 : 0}
        listening={false}
        perfectDrawEnabled={false}
      />

      {/* Animated flow overlay — contrasting dashes over the base line */}
      {shouldAnimate && (
        <Line
          ref={flowRef}
          points={finalLinePoints}
          stroke={brightenColor(strokeOf(palette.edgeDefault), 0.55)}
          strokeWidth={Math.max(2, sw * 0.9)}
          bezier={useBezier} tension={useTension ? 0.4 : 0}
          dash={[10, 10]}
          dashOffset={0}
          opacity={0.9}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}

      {/* Endpoint caps: which end is the tail and which is the head, without
          needing a socket ring on the node. Indigo leaves an output, emerald
          arrives at an input — the same polarity the sockets use on hover. */}
      {showEndpointCaps && (
        <>
          <Circle
            x={startPoint.x} y={startPoint.y} radius={3.2}
            fill={EDGE_TAIL_COLOR} opacity={0.85}
            listening={false} perfectDrawEnabled={false}
          />
          <Circle
            x={endPoint.x} y={endPoint.y} radius={4.4}
            fill={EDGE_HEAD_COLOR} opacity={0.22}
            listening={false} perfectDrawEnabled={false}
          />
        </>
      )}

      {/* Forward arrowhead (target end) */}
      {forwardArrow.length > 0 && (
        <Line points={forwardArrow} fill={arrowColor} stroke={arrowColor}
          strokeWidth={1} closed listening={false} perfectDrawEnabled={false} />
      )}

      {inheritanceHead.length > 0 && (
        <Line points={inheritanceHead} fill={palette.nodeBody} stroke={arrowColor}
          strokeWidth={Math.max(1.5, sw)} closed listening={false} perfectDrawEnabled={false} />
      )}

      {sourceDiamond.length > 0 && (
        <Line
          points={sourceDiamond}
          fill={edgeType === 'composition' ? arrowColor : palette.nodeBody}
          stroke={arrowColor}
          strokeWidth={Math.max(1.5, sw)}
          closed
          listening={false}
          perfectDrawEnabled={false}
        />
      )}

      {/* Reverse arrowhead (source end — bidirectional only) */}
      {reverseArrow.length > 0 && (
        <Line points={reverseArrow} fill={arrowColor} stroke={arrowColor}
          strokeWidth={1} closed listening={false} perfectDrawEnabled={false} />
      )}

      {/* Waypoint anchor handles — visible and draggable when selected */}
      {!isReadOnly && isSelected && waypoints.map((wp, i) => (
        <Circle
          key={i}
          x={wp.x} y={wp.y}
          radius={6}
          fill={palette.edgeSelected}
          stroke="white" strokeWidth={1.5}
          draggable
          onClick={(e) => e.cancelBubble = true}
          onContextMenu={(e) => e.cancelBubble = true}
          onDblClick={(e) => {
            e.cancelBubble = true;
            const newWaypoints = waypoints.filter((_, idx) => idx !== i);
            onWaypointUpdate?.(edge.id, newWaypoints);
          }}
          onDragEnd={(e) => {
            const newWaypoints = waypoints.map((w, idx) =>
              idx === i ? { x: e.target.x(), y: e.target.y() } : w
            );
            onWaypointUpdate?.(edge.id, newWaypoints);
          }}
          onMouseEnter={(e) => {
            const container = e.target.getStage()?.container();
            if (container) container.style.cursor = 'move';
          }}
          onMouseLeave={(e) => {
            const container = e.target.getStage()?.container();
            if (container) container.style.cursor = 'default';
          }}
          perfectDrawEnabled={false}
        />
      ))}

      {/* Edge messages (new multi-message system) */}
      {(() => {
        // Collect all messages: from edge.messages array, with fallback for legacy edge.label
        const msgs: EdgeMessage[] = edge.messages && edge.messages.length > 0
          ? edge.messages
          : (edge.label ? [{ id: 'legacy', label: edge.label, distanceOffset: 0.5 }] : []);

        if (!msgs.length || linePoints.length < 4) return null;

        // For bezier edges (single cubic), sample the actual curve so labels sit ON the
        // visible curve rather than on the control-point polygon.
        const displayPolyline = buildDisplayPolyline(linePoints, useBezier);

        // The box is measured from these, so scaling both keeps the padding
        // around a label the same whatever size the page draws it at.
        const FONT_H = scaleFont(12, textScale), LINE_H = scaleFont(17, textScale), PAD_X = 10, PAD_Y = 6;

        return msgs.map((msg) => {
          const useOrthoAnchor = isOrthogonal && labelPos && Math.abs(msg.distanceOffset - 0.5) < 0.001;
          const pos = useOrthoAnchor ? labelPos : pointOnPolyline(displayPolyline, msg.distanceOffset);
          if (!pos) return null;

          // Hide label while its textarea overlay is active
          if (msg.id === editingMsgId) return null;

          const measured = measureLabelSize(msg.label, `${FONT_H}px ${FONT_FAMILY}`, LINE_H);
          const boxW = Math.max(measured.width + PAD_X * 2, 56);
          const boxH = Math.max(measured.height + PAD_Y * 2, LINE_H + PAD_Y * 2);
          const halfW = boxW / 2;
          const halfH = boxH / 2;

          return (
            <Group
              key={msg.id}
              x={pos.x}
              y={pos.y}
              draggable={!isReadOnly && !!onEdgeMessageUpdate}
              dragBoundFunc={(newPos) => newPos}
              onDblClick={(e) => {
                e.cancelBubble = true;
                if (!isReadOnly && onEdgeMessageDblClick) {
                  // Pass canvas-space centre + box dimensions so App.tsx can compute an exact overlay
                  onEdgeMessageDblClick(edge.id, msg.id, pos.x, pos.y, boxW, boxH);
                }
              }}
              onDragMove={(e) => {
                if (!onEdgeMessageUpdate || isReadOnly) return;
                // Convert stage pointer → canvas coords via the viewport group's transform,
                // then snap the group position to the nearest point on the actual curve.
                const stage = e.target.getStage();
                const pointer = stage?.getPointerPosition();
                const viewportGroup = e.target.getParent()?.getParent();
                const transform = viewportGroup?.getAbsoluteTransform().copy().invert();
                if (!pointer || !transform) return;
                const cp = transform.point(pointer);
                const snappedOffset = closestOffsetOnPolyline(displayPolyline, cp.x, cp.y);
                const snapPos = pointOnPolyline(displayPolyline, snappedOffset);
                if (snapPos) e.target.position(snapPos);
              }}
              onDragEnd={(e) => {
                if (!onEdgeMessageUpdate || isReadOnly) return;
                // The group was continuously snapped to the curve during drag,
                // so its current x/y (canvas coords) are already on the polyline.
                const canvasX = e.target.x();
                const canvasY = e.target.y();
                const newOffset = closestOffsetOnPolyline(displayPolyline, canvasX, canvasY);
                const updated = (edge.messages ?? [{ id: 'legacy', label: edge.label ?? '', distanceOffset: 0.5 }]).map(m =>
                  m.id === msg.id ? { ...m, distanceOffset: Math.max(0.05, Math.min(0.95, newOffset)) } : m
                );
                onEdgeMessageUpdate(edge.id, updated);
              }}
              onMouseEnter={(e) => {
                const container = e.target.getStage()?.container();
                if (container) container.style.cursor = 'grab';
              }}
              onMouseLeave={(e) => {
                const container = e.target.getStage()?.container();
                if (container) container.style.cursor = 'default';
              }}
            >
              <Rect
                x={-halfW}
                y={-halfH}
                width={boxW}
                height={boxH}
                fill={toRgba(palette.canvasBg, LABEL_HALO_ALPHA)}
                cornerRadius={4}
                listening={false}
                perfectDrawEnabled={false}
              />
              <Text
                text={msg.label}
                x={-halfW + PAD_X}
                y={-halfH + PAD_Y}
                width={boxW - PAD_X * 2}
                align="center"
                wrap="word"
                fontSize={FONT_H}
                fontFamily={FONT_FAMILY}
                fill={palette.panelText}
                lineHeight={LINE_H / FONT_H}
                perfectDrawEnabled={false}
                listening={false}
              />
            </Group>
          );
        });
      })()}

      {/* Discovery mode — connection info (hover only, show when no messages) */}
      {discoveryMode && hovered && !edge.label && !edge.messages?.length && discoveryText && labelPos && (() => {
        const discoverySize = measureLabelSize(discoveryText, `9px ${FONT_FAMILY}`, 14);
        const discoveryW = Math.max(discoverySize.width + 8, 40);
        return (
        <Group x={labelPos.x} y={labelPos.y}>
          <Rect
            offsetX={discoveryW / 2}
            offsetY={7}
            width={discoveryW}
            height={14}
            fill={toRgba(palette.canvasBg, LABEL_HALO_ALPHA)}
            cornerRadius={3}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Text
            text={discoveryText}
            fontSize={9}
            fontFamily={FONT_FAMILY}
            fill={palette.panelSubText}
            align="center"
            offsetX={discoveryW / 2}
            offsetY={4}
            width={discoveryW}
            listening={false}
            perfectDrawEnabled={false}
          />
        </Group>
        );
      })()}
    </Group>
  );
};

/**
 * For a single cubic bezier (8-point flat array [x0,y0,cp1x,cp1y,cp2x,cp2y,x1,y1]),
 * sample N+1 evenly-spaced points and return as a flat polyline array.
 * For all other point counts (orthogonal, tension, multi-segment) the original array
 * is returned unchanged so the straight-segment fallback still works.
 */
function buildDisplayPolyline(pts: number[], useBezier: boolean, N = 48): number[] {
  if (!useBezier || pts.length !== 8) return pts;
  const [x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1] = pts;
  const result: number[] = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const mt = 1 - t;
    result.push(
      mt * mt * mt * x0 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x1,
      mt * mt * mt * y0 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y1,
    );
  }
  return result;
}

/** Return an (x, y) point at fraction `t` along a polyline defined by flat [x0,y0,x1,y1,...] array. */
function pointOnPolyline(pts: number[], t: number): { x: number; y: number } | null {
  if (pts.length < 4) return null;
  const clampedT = Math.max(0, Math.min(1, t));

  // Build segment lengths
  const segLens: number[] = [];
  let totalLen = 0;
  for (let i = 0; i < pts.length - 2; i += 2) {
    const dx = pts[i + 2] - pts[i];
    const dy = pts[i + 3] - pts[i + 1];
    const l = Math.sqrt(dx * dx + dy * dy);
    segLens.push(l);
    totalLen += l;
  }
  if (totalLen === 0) return { x: pts[0], y: pts[1] };

  const target = clampedT * totalLen;
  let acc = 0;
  for (let i = 0; i < segLens.length; i++) {
    const segLen = segLens[i];
    if (acc + segLen >= target) {
      const frac = segLen === 0 ? 0 : (target - acc) / segLen;
      return {
        x: pts[i * 2] + frac * (pts[i * 2 + 2] - pts[i * 2]),
        y: pts[i * 2 + 1] + frac * (pts[i * 2 + 3] - pts[i * 2 + 1]),
      };
    }
    acc += segLen;
  }
  // Fallback: last point
  return { x: pts[pts.length - 2], y: pts[pts.length - 1] };
}

/** Return the t value [0,1] of the closest point on the polyline to (cx, cy). */
function closestOffsetOnPolyline(pts: number[], cx: number, cy: number): number {
  if (pts.length < 4) return 0.5;
  let totalLen = 0;
  const segLens: number[] = [];
  for (let i = 0; i < pts.length - 2; i += 2) {
    const dx = pts[i + 2] - pts[i];
    const dy = pts[i + 3] - pts[i + 1];
    segLens.push(Math.sqrt(dx * dx + dy * dy));
    totalLen += segLens[segLens.length - 1];
  }
  if (totalLen === 0) return 0.5;

  let bestDist = Infinity, bestOffset = 0.5, acc = 0;
  for (let i = 0; i < segLens.length; i++) {
    const x0 = pts[i * 2], y0 = pts[i * 2 + 1];
    const x1 = pts[i * 2 + 2], y1 = pts[i * 2 + 3];
    const dx = x1 - x0, dy = y1 - y0;
    const segLen = segLens[i];
    const t = segLen === 0 ? 0 : Math.max(0, Math.min(1, ((cx - x0) * dx + (cy - y0) * dy) / (segLen * segLen)));
    const px = x0 + t * dx, py = y0 + t * dy;
    const dist = (cx - px) ** 2 + (cy - py) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      bestOffset = (acc + t * segLen) / totalLen;
    }
    acc += segLen;
  }
  return bestOffset;
}
